import { useEffect, useRef } from 'react';

import { useBetslipStore } from '@/store/betslip';
import { getNonNormalizedSports } from '@/services/settings.service';
import { smpService } from '@/features/single-match-parlay/services/smp.service';
import { compose } from '@nf/utils-common/fpw/libs';
import { idealRoundFixed } from '@nf/utils-common/numbers';
import { removeCommas } from '@/utils/odds-utils';
import { useAccountSabaCoinWallet } from '@/hooks/query/account';
import { useTranslations } from 'next-intl';
import { SabaCoinIcon } from '@/components/icons/saba-coin-icon';
import { cx } from 'styled-system/css';
import { coinsStyles } from './saba-coins-toggle.style';
import { usePromoShine } from '@/features/saba-coins/promo-shine';
import { RnrButton } from '@/features/saba-coins/saba-coins-toggle/rnr-button';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';

const toggleFrequency = 1000;
export const SabaCoinsToggle = () => {
	const { data: sabaCoinWallet } = useAccountSabaCoinWallet();
	const availableCoins = compose(Number, removeCommas)(sabaCoinWallet?.AvailableFund ?? '0');
	const { Component: PromoShine, markPromoShineAsCompleted } = usePromoShine();
	const t = useTranslations('General');
	const toggleTime = useRef<number>(new Date().getTime()-toggleFrequency);
	const { SiteConfigsData } = useGetSiteConfigs();

	const [
		betslipType,
		currentMultiTickets,
		isBookingBet,
		isQuickBet,
		useSabaCoin,
		setUseSabaCoin,
	] = useBetslipStore(
		state => [
			state.betslipType,
			state.currentMultiTickets,
			state.isBookingBet,
			state.isQuickBet,
			state.useSabaCoin,
			state.setUseSabaCoin,
		]
	);

	const ticket = currentMultiTickets[0];
	const isSmp = smpService.isSmpTicket(ticket);
	const isSupportedSport = !getNonNormalizedSports().has(String(ticket.SportType));

	const showSabaCoins = sabaCoinWallet 
		&& SiteConfigsData?.EnableSabaCoinBet
		&& !isBookingBet
		&& !isQuickBet
		&& !isSmp
		&& isSupportedSport;

	useEffect(() => {
		return () => {
			setUseSabaCoin(false);
		};
	}, []);

	const handleCoinsToggle = () => {
		const kafkaData = {
			note: KafkaNote.Reward,
			type: 7,
			action: useSabaCoin ? 2 : 1 
		}
		setKafkaNormal(kafkaData);
		const currentTime = new Date().getTime();
		if (currentTime - toggleTime.current >= toggleFrequency) {
			setUseSabaCoin(!useSabaCoin);
			markPromoShineAsCompleted(!useSabaCoin);
			toggleTime.current = currentTime;
		}
	};

	return showSabaCoins ? (
		<div className={cx('bet-slip__coin', coinsStyles.coin)}>
			<div className="bet-slip__coin-item">
				<RnrButton />
				<div className={cx('bet-slip__coin-label', coinsStyles.label)}>{t('lbl_useSBC')}</div>
			</div>
			<div className="bet-slip__coin-value">
				<PromoShine />
				<div className={cx('bet-slip__coin-title', coinsStyles.title)}>
					<div className={cx('bet-slip__coin-text', coinsStyles.text)}>{SiteConfigsData?.EnableCurrencyDisplay ? 'SBC' : ''}</div>
					{idealRoundFixed(availableCoins, 2)}
				</div>
				<div 
					className={cx('switch switch--coin', coinsStyles.switchCoin)}
					data-active={useSabaCoin}
					onClick={handleCoinsToggle}
				>
					<div className="switch__thumb">
						<SabaCoinIcon />
					</div>
				</div>
			</div>
		</div>
	) : null;
};